import { useEffect, useMemo, useState } from 'react';
import { useReservoirClient, useTokens } from '@reservoir0x/reservoir-kit-ui';
import { useGate } from 'statsig-react';
import { useBalance } from 'wagmi';
import { ContractMethod } from '@soundxyz/utils/const';
import {
  ERROR_TYPE,
  MINTING_ERROR_ACTIONS,
  MINTING_INFO_ACTIONS,
  PILLARS,
} from '@soundxyz/utils/constants/logging';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { useToast } from '@context/toast';
import { Transaction } from '@context/transaction';
import { getTransactionResponse } from '@context/transaction/TransactionStatusStore';
import { trackClickEvent } from '@lib/analytics';
import { FEATURE_GATES } from '@utils/constants';
import { formatWeiToEthDisplayFree } from '@utils/helpers';
import { logError } from './useLogErrorNew';
import { logInfo } from './useLogInfo';

export type MarketPlaceCollectionType =
  | 'V1 Edition'
  | 'Artist Contract'
  | 'Album Edition'
  | 'Tiered Edition';

export const useMarketPlace = ({
  quantity,
  releaseId,
  contractAddress,
  contractChainId,
  releaseTitle,
  collectionType,
}: {
  releaseId: string;
  contractAddress: string;
  contractChainId: number;
  quantity: number;
  releaseTitle: string;
  collectionType: MarketPlaceCollectionType;
}) => {
  const { authUserAddress } = useAuth();
  const { value: marketPlaceEnabled } = useGate(FEATURE_GATES.BUY_ON_SOUND_MARKET);

  const { getSigner, expectMatchingChain, isSwitchingNetwork, provider, getDeepLink } =
    useConnectedAuth();
  const [isCollecting, setIsCollecting] = useState(false);
  const [currentTxHash, setCurrentTxHash] = useState<string | null>(null);
  const { openToast } = useToast();
  const client = useReservoirClient();

  const { data: ethBalance } = useBalance({
    address: authUserAddress,
    enabled: !!authUserAddress,
    formatUnits: 'wei',
    chainId: contractChainId,
  });

  const { handleTransaction } = Transaction.useContainer();

  const attributesToFilter = useMemo(() => {
    switch (collectionType) {
      case 'Artist Contract':
        return {
          [`attributes[${releaseTitle}]`]: ['Song Edition', 'Golden Egg'],
        };
      case 'Album Edition':
        return { [`attributes[SINGLE]`]: releaseTitle };
      case 'Tiered Edition':
        return { [`attributes[Tier]`]: ['Limited'] };
      default:
        return {};
    }
  }, [collectionType, releaseTitle]);

  // Reservoir created separate collections for cases where we had multiple songs in the same contract
  // for album editions and artist contracts as part of custom sound market work
  const reservoirCollectionId =
    collectionType === 'Album Edition' || collectionType === 'Artist Contract'
      ? `${contractAddress}:soundxyz-${releaseId}`
      : contractAddress;

  const {
    data: tokens,
    isLoading: isLoadingTokens,
    isFetchingPage,
    fetchNextPage,
    hasNextPage,
    mutate: refetchTokensData,
  } = useTokens(
    {
      collection: reservoirCollectionId,
      limit: 100,
      sortBy: 'floorAskPrice',
      normalizeRoyalties: true,
      ...attributesToFilter,
    },
    {},
    contractChainId,
  );

  const isLoading = marketPlaceEnabled && isLoadingTokens;

  const filteredTokens = useMemo(() => {
    return tokens.filter(
      ({ token, market }) => !!market?.floorAsk?.price && token?.owner !== authUserAddress,
    );
  }, [tokens, authUserAddress]);

  useEffect(() => {
    if (hasNextPage && !isLoadingTokens && quantity === filteredTokens.length) {
      fetchNextPage();
    }
  }, [
    fetchNextPage,
    filteredTokens.length,
    hasNextPage,
    isFetchingPage,
    isLoadingTokens,
    quantity,
  ]);

  const marketPlaceFloorPriceInWei = useMemo(
    () => filteredTokens[0]?.market?.floorAsk?.price?.amount?.raw ?? null,
    [filteredTokens],
  );

  const totalCost = useMemo(() => {
    const tokensToBuy = filteredTokens.slice(0, quantity);
    return tokensToBuy.reduce(
      (acc, token) => {
        const amountInWei = BigInt(token.market?.floorAsk?.price?.amount?.raw ?? 0);
        const amountInUsd = Number(token.market?.floorAsk?.price?.amount?.usd ?? 0);
        return { wei: acc.wei + amountInWei, usd: acc.usd + amountInUsd };
      },
      { wei: BigInt(0), usd: 0 },
    );
  }, [filteredTokens, quantity]);

  const isSufficientFunds = useMemo(() => {
    return totalCost.wei <= (ethBalance?.value ?? 0n);
  }, [ethBalance?.value, totalCost.wei]);

  const marketPlaceFloorPriceInEth =
    marketPlaceEnabled && marketPlaceFloorPriceInWei
      ? formatWeiToEthDisplayFree({ weiValue: marketPlaceFloorPriceInWei, maxDecimals: 4 })
      : null;

  const handleMarketPlaceTransaction = useStableCallback(async () => {
    if (currentTxHash) {
      try {
        const transactionFn = () => {
          return getTransactionResponse({
            hash: currentTxHash,
            chainId: contractChainId,
            provider: provider(contractChainId),
          });
        };
        const status = await handleTransaction({
          contractMethod: ContractMethod.ARTIST__BUY_EDITION,
          transactionFn,
          chainId: contractChainId,
          openProcessingModal: true,
          showOpenWalletToast: false,
          releaseId,
        });
        if (status) {
          trackClickEvent({
            event: 'Pay Marketplace with ETH Success',
            properties: {
              associationId: releaseId,
              associationType: 'Release',
              quantity,
              chainId: contractChainId,
              contract: contractAddress,
              // In future we should pass in the source component for analytics
              sourceComponent: 'Collect Via Marketplace Modal',
            },
          });
        }
        await refetchTokensData();
      } catch (error) {
        openToast({ text: 'Error when collecting tokens.', variant: 'error' });
        logError({
          action: MINTING_ERROR_ACTIONS.BUY_MARKEPLACE_TOKEN,
          error,
          toast: 'Error while buying via marketplace',
          message: `Error while buying via marketplace`,
          errorType: ERROR_TYPE.RESERVOIR_MINT,
          indexedTags: {
            releaseId,
            selectedQuantity: quantity,
            chainId: contractChainId,
          },
          isArtistOnlyPage: false,
          pillar: PILLARS.MINTING,
          level: 'fatal',
        });
      } finally {
        setCurrentTxHash(null);
        setIsCollecting(false);
      }
    }
  });

  const collectMarketPlace = useStableCallback(async ({ quantity }: { quantity: number }) => {
    setIsCollecting(true);
    const chain = expectMatchingChain(contractChainId, {
      onSyncSwitchSuccess() {
        openToast({
          text: `Successfully switched to Ethereum.\nTap Bridge to continue.`,
          variant: 'success',
        });
      },
    });
    if (chain.stop) {
      return setIsCollecting(false);
    }
    try {
      if (chain.switching) {
        await chain.switching;
      }
    } catch (error) {
      openToast({
        text: 'Please switch to ethereum mainnet',
        variant: 'error',
      });
      setIsCollecting(false);
      return;
    }
    if (!client) {
      setIsCollecting(false);
      logError({
        action: MINTING_ERROR_ACTIONS.BUY_MARKEPLACE_TOKEN,
        error: {},
        toast: 'Error while collecting via marketplace',
        message: `Reservoir client not found`,
        errorType: ERROR_TYPE.RESERVOIR_MINT,
        indexedTags: {
          releaseId,
          selectedQuantity: quantity,
          chainId: contractChainId,
        },
        isArtistOnlyPage: false,
        pillar: PILLARS.MINTING,
        level: 'fatal',
      });
      return openToast({ text: 'Error when collecting tokens.', variant: 'error' });
    }
    const tokensToBuy = filteredTokens.slice(0, quantity);
    if (tokensToBuy.some(({ token }) => !token || !token?.contract || !token?.tokenId)) {
      setIsCollecting(false);
      return logError({
        action: MINTING_ERROR_ACTIONS.BUY_MARKEPLACE_TOKEN,
        error: { tokensToBuy },
        toast: 'Error while collecting via marketplace',
        message: `Tokens to buy have invalid data`,
        errorType: ERROR_TYPE.RESERVOIR_MINT,
        indexedTags: {
          releaseId,
          selectedQuantity: quantity,
          chainId: contractChainId,
        },
        isArtistOnlyPage: false,
        pillar: PILLARS.MINTING,
        level: 'fatal',
      });
    }
    try {
      const signer = await getSigner(contractChainId);
      openToast({
        text: 'Please confirm the transaction in your wallet.',
      });
      logInfo({
        action: MINTING_INFO_ACTIONS.EDITION_MINT,
        message: 'Buy from marketplace',
        data: {
          releaseId: releaseId,
          chainId: contractChainId,
          minterAddress: authUserAddress,
        },
        pillar: PILLARS.MINTING,
        isArtistOnlyPage: false,
      });
      /**
       * This is critical for a better mobile UX.
       * We have to set before the actual transaction is sent.
       * This way it can open the user's wallet app and make it a seamless flow
       */
      const deepLink = getDeepLink();
      if (deepLink) {
        window.location.href = deepLink;
      }
      await client.actions.buyToken({
        items: filteredTokens.slice(0, quantity).map(({ token }) => ({
          fillType: 'trade',
          token: `${token?.contract}:${token?.tokenId}`,
        })),
        wallet: signer,
        onProgress(progress) {
          const progressEvent = progress?.[0]?.items?.[0];
          if (!currentTxHash && progressEvent && progressEvent.txHash) {
            setCurrentTxHash(progressEvent.txHash);
          }
        },
        chainId: contractChainId,
      });
    } catch (error) {
      openToast({ text: 'Error when collecting tokens.', variant: 'error' });
      logError({
        action: MINTING_ERROR_ACTIONS.EDITION_MINT_ERROR,
        error,
        toast: 'Error while collecting via marketplace',
        message: `Errored while collecting via marketplace`,
        errorType: ERROR_TYPE.EDITION_MINT,
        indexedTags: {
          releaseId,
          selectedQuantity: quantity,
          chainId: contractChainId,
        },
        isArtistOnlyPage: false,
        pillar: PILLARS.MINTING,
        level: 'fatal',
      });
      setIsCollecting(false);
    }
  });

  useEffect(() => {
    if (currentTxHash) {
      handleMarketPlaceTransaction();
    }
  }, [currentTxHash, handleMarketPlaceTransaction]);

  const state = useMemo(() => {
    if (!isSufficientFunds) {
      return 'Insufficient funds';
    }
    if (isSwitchingNetwork) {
      return 'Switch chain in wallet';
    }
    if (isCollecting) {
      return 'Confirm in wallet';
    }
    return 'Pay with ETH';
  }, [isCollecting, isSufficientFunds, isSwitchingNetwork]);

  return {
    isLoading,
    marketPlaceFloorPriceInWei,
    collectMarketPlace,
    marketPlaceTokens: filteredTokens,
    isCollecting,
    isSwitchingNetwork,
    state,
    totalCost,
    isSufficientFunds,
    fetchNextPage,
    hasNextPage,
    marketPlaceEnabled,
    marketPlaceFloorPriceInEth,
  };
};
